import { ApplicationRef, Directive, ElementRef, Optional } from '@angular/core';
import { MatTable, MatTableDataSource } from '@angular/material/table';

export enum ExportType {
    CSV = 'csv',
}

export interface ExportOptions {
    fileName?: string;
    /** Accepted and ignored; mat-table-exporter passed spreadsheet metadata here. */
    Props?: Record<string, unknown>;
}

/**
 * Drop-in replacement for mat-table-exporter's directive.
 *
 * That package reaches xlsx 0.18.5 through cdk-table-exporter, which carries an unfixed
 * prototype-pollution advisory: SheetJS stopped publishing to npm at that version, so no upgrade
 * exists. The app only ever wrote CSV -- ExportType.CSV, from one component -- so the whole
 * spreadsheet engine was 414 kB of bundle for a code path it never took.
 *
 * The selector, exportAs name and exportTable signature are kept so the seven templates and
 * GlobalExportCsvComponent are unchanged.
 *
 * Rows come from the rendered table rather than the data source, so what lands in the file is what
 * the columns actually display -- pipes, nested properties and all -- and it follows the current
 * sort and filter. Paginated tables are temporarily expanded to their full length first, because
 * exporting only the visible page is not what this replaced.
 */
@Directive({
    selector: '[matTableExporter]',
    exportAs: 'matTableExporter',
    standalone: false,
})
export class TableExportDirective {
    constructor(
        private readonly host: ElementRef<HTMLElement>,
        private readonly appRef: ApplicationRef,
        @Optional() private readonly table?: MatTable<unknown>
    ) {}

    async exportTable(type: ExportType = ExportType.CSV, options: ExportOptions = {}): Promise<void> {
        const paginator = this.paginator();
        const restore = paginator ? { pageIndex: paginator.pageIndex, pageSize: paginator.pageSize } : null;

        try {
            if (paginator && restore) {
                await this.showAllRows(paginator);
            }
            const csv = this.toCsv(this.readRows());
            this.download(csv, `${options.fileName ?? 'export'}.csv`);
        } finally {
            if (paginator && restore) {
                await this.applyPage(paginator, restore.pageIndex, restore.pageSize);
            }
        }
    }

    /** @return the paginator driving this table, when it has one. */
    private paginator() {
        return this.source()?.paginator ?? null;
    }

    private source(): MatTableDataSource<unknown> | null {
        const source = this.table?.dataSource;
        return source instanceof MatTableDataSource ? source : null;
    }

    private async showAllRows(paginator: { length: number }): Promise<void> {
        // Count the rows off the data source, not off paginator.length: MatTableDataSource assigns
        // that in a microtask, so just after a paginator or filter is attached it is still 0, and
        // expanding to a page of zero rows would export an empty table.
        const total = this.source()?.filteredData.length ?? paginator.length;
        await this.applyPage(paginator as never, 0, Math.max(total, 1));
    }

    private async applyPage(
        paginator: { pageIndex: number; pageSize: number; length: number; page: { emit(event: unknown): void } },
        pageIndex: number,
        pageSize: number
    ): Promise<void> {
        paginator.pageIndex = pageIndex;
        paginator.pageSize = pageSize;
        // MatTableDataSource re-slices its rows off this event; nothing happens without it.
        paginator.page.emit({ pageIndex, pageSize, length: paginator.length });
        // Let the table re-render before the DOM is read back. tick() rather than trusting the
        // zone, so this also holds under TestBed, where nothing runs change detection for us.
        await new Promise<void>((resolve) => setTimeout(resolve));
        this.appRef.tick();
    }

    /** @return the rendered header and body rows, as arrays of cell text. */
    private readRows(): string[][] {
        const table = this.host.nativeElement;
        const rows: string[][] = [];

        for (const row of Array.from(table.querySelectorAll('tr'))) {
            const cells = Array.from(row.querySelectorAll('th, td'));
            if (cells.length === 0) {
                continue;
            }
            // A "no data" row is one cell spanning the table; it is not a record.
            if (cells.length === 1 && row.querySelector('td[colspan]')) {
                continue;
            }
            rows.push(cells.map((cell) => (cell.textContent ?? '').replace(/\s+/g, ' ').trim()));
        }
        return rows;
    }

    private toCsv(rows: string[][]): string {
        return rows.map((row) => row.map((cell) => this.escape(cell)).join(',')).join('\r\n');
    }

    private escape(value: string): string {
        return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
    }

    private download(csv: string, fileName: string): void {
        // The BOM is what makes Excel read the file as UTF-8 rather than the local codepage.
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
}
